import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Factory as FactoryIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/PageHeader';
import { elevatedPanelSx } from '../../constants/shape';
import StatusChip from '../../components/StatusChip';

const ProcessingCenters: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    location: '',
    capacity: '',
    currentLoad: '',
    status: 'Active',
    manager: '',
    contactNumber: '',
    email: '',
    operatingHours: '',
    isActive: true,
    notes: '',
  });

  // Mock data for demonstration
  const processingCenters = [
    {
      id: '1',
      name: 'Central Processing Facility',
      type: 'Mixed Waste',
      location: '123 Industrial Park, City',
      capacity: '100 tons/day',
      currentLoad: '75%',
      status: 'Active',
      manager: 'John Smith',
      contactNumber: '+1 234 567 8900',
      email: 'john.smith@company.com',
      operatingHours: '24/7',
      isActive: true,
      currentWasteType: 'Mixed Municipal',
      processedToday: '75 tons',
    },
    // Add more mock centers as needed
  ];

  const centerTypes = [
    'Mixed Waste',
    'Plastic',
    'Paper',
    'Metal',
    'Glass',
    'Organic',
    'Hazardous',
  ];

  const handleOpen = (center?: any) => {
    if (center) {
      setSelectedCenter(center);
      setFormData({
        name: center.name,
        type: center.type,
        location: center.location,
        capacity: center.capacity,
        currentLoad: center.currentLoad,
        status: center.status,
        manager: center.manager,
        contactNumber: center.contactNumber,
        email: center.email,
        operatingHours: center.operatingHours,
        isActive: center.isActive,
        notes: '',
      });
    } else {
      setSelectedCenter(null);
      setFormData({
        name: '',
        type: '',
        location: '',
        capacity: '',
        currentLoad: '',
        status: 'Active',
        manager: '',
        contactNumber: '',
        email: '',
        operatingHours: '',
        isActive: true,
        notes: '',
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedCenter(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    handleClose();
  };

  return (
    <Box>
      <PageHeader
        icon={<FactoryIcon />}
        title={t('processing.centers.title')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{
              backgroundColor: theme.palette.primary.main,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              borderRadius: '12px',
              px: 3,
              py: 1.5,
            }}
          >
            {t('processing.centers.addCenter')}
          </Button>
        }
      />

      <Paper elevation={2} sx={elevatedPanelSx}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.centers.center')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.type')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.location')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.currentLoad')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.centers.currentWaste')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('processing.centers.processedToday')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.manager')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processingCenters.map((center) => (
                <TableRow 
                  key={center.id}
                  sx={{ 
                    '&:hover': { 
                      bgcolor: theme.palette.action.hover,
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FactoryIcon sx={{ color: theme.palette.primary.main }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {center.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('processing.centers.capacityLabel', { value: center.capacity })}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{center.type}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationIcon sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
                      <Typography variant="body2">
                        {center.location}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={parseInt(center.currentLoad)} 
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            bgcolor: theme.palette.primary.main,
                          },
                        }}
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {center.currentLoad}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{center.currentWasteType}</TableCell>
                  <TableCell>{center.processedToday}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {center.manager}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {center.contactNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip label={center.status} value={center.status} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => handleOpen(center)}
                      sx={{ 
                        color: theme.palette.primary.main,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.light,
                          color: theme.palette.primary.contrastText,
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      sx={{ 
                        color: theme.palette.error.main,
                        '&:hover': { 
                          bgcolor: theme.palette.error.light,
                          color: theme.palette.error.contrastText,
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: "16px 16px 0 0",
        }}>
          {selectedCenter ? t('processing.centers.editDialogTitle') : t('processing.centers.addDialogTitle')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.centerName')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.type')}</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label={t('common.type')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    {centerTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('common.location')}
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.capacity')}
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('processing.centers.capacityPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.currentLoad')}
                  value={formData.currentLoad}
                  onChange={(e) => setFormData({ ...formData, currentLoad: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('processing.centers.currentLoadPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>{t('common.status')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    label={t('common.status')}
                    sx={{
                      borderRadius: '8px',
                    }}
                  >
                    <MenuItem value="Active">{t('common.active')}</MenuItem>
                    <MenuItem value="Maintenance">{t('containers.status.maintenance')}</MenuItem>
                    <MenuItem value="Inactive">{t('common.inactive')}</MenuItem>
                    <MenuItem value="Error">{t('common.unknown')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.operatingHours')}
                  value={formData.operatingHours}
                  onChange={(e) => setFormData({ ...formData, operatingHours: e.target.value })}
                  required
                  fullWidth
                  placeholder={t('processing.centers.operatingHoursPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.manager')}
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('common.contactNumber')}
                  value={formData.contactNumber}
                  onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('common.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('common.active')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('common.notes')}
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button
              onClick={handleClose}
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                },
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                borderRadius: '8px',
                px: 3,
              }}
            >
              {selectedCenter ? t('common.update') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ProcessingCenters; 